// MarkdownEditor — the shared rich-text editor that reads & writes Markdown.
//
// Built on TipTap (ProseMirror) + tiptap-markdown so the on-the-wire value is
// plain Markdown — the same body_markdown shape Link produces and NeedView
// renders. Use anywhere we want authored rich text (headings, bold, lists)
// without exposing raw markdown syntax. `value`/`onChange` are markdown strings.

import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import { BubbleMenu, FloatingMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { Markdown } from 'tiptap-markdown';
import {
    Bold, Italic, Heading2, Heading3, List, ListOrdered, Link as LinkIcon, Undo2, Redo2,
    ImagePlus, Loader2,
} from 'lucide-react';
import { LinkAssistPopover } from './LinkAssistPopover';

interface MarkdownEditorProps {
    value: string;
    onChange: (markdown: string) => void;
    placeholder?: string;
    /** Minimum editor height (Tailwind class), default min-h-[160px]. */
    minHeightClass?: string;
    /** Extra controls rendered at the right of the toolbar (e.g. a Link assist button). */
    toolbarExtra?: React.ReactNode;
    /** Enable image support + a toolbar upload button. The callback uploads the
     *  file and returns the URL to embed (markdown `![](url)`). */
    onImageUpload?: (file: File) => Promise<string>;
    /** Enable a "Fråga Link" action in the selection bubble menu. Given the
     *  selected text, returns the revised text to replace the selection with. */
    selectionAssistRun?: (action: string, instruction: string, selectedText: string) => Promise<string>;
}

const getMarkdown = (editor: Editor): string => {
    // tiptap-markdown exposes this on storage; not in the published types.
    return (editor.storage as any)?.markdown?.getMarkdown?.() ?? '';
};

const ToolbarButton: React.FC<{
    onClick: () => void;
    active?: boolean;
    disabled?: boolean;
    title: string;
    children: React.ReactNode;
}> = ({ onClick, active, disabled, title, children }) => (
    <button
        type="button"
        title={title}
        onClick={onClick}
        disabled={disabled}
        className={`p-1.5 rounded-md transition-colors disabled:opacity-30 ${
            active ? 'bg-[#A4C3B2]/40 text-[#1A4D4F]' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
        }`}
    >
        {children}
    </button>
);

export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
    value,
    onChange,
    placeholder,
    minHeightClass = 'min-h-[160px]',
    toolbarExtra,
    onImageUpload,
    selectionAssistRun,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    // Remembers the selection range the bubble-menu Fråga Link ran on, so the
    // revised text replaces exactly that range even after focus moved to the popover.
    const selRangeRef = useRef<{ from: number; to: number } | null>(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imageError, setImageError] = useState<string | null>(null);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [2, 3] } }),
            Link.configure({ openOnClick: false, autolink: true }),
            Image.configure({ inline: false, HTMLAttributes: { class: 'rounded-lg max-w-full h-auto' } }),
            Placeholder.configure({ placeholder: placeholder || 'Skriv här…' }),
            Markdown.configure({ html: false, tightLists: true, linkify: true, transformPastedText: true }),
        ],
        content: value || '',
        editorProps: {
            attributes: {
                class: `prose prose-sm max-w-none focus:outline-none px-3.5 py-2.5 ${minHeightClass} [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold`,
            },
        },
        onUpdate: ({ editor }) => onChange(getMarkdown(editor)),
    });

    // Sync external value changes (e.g. AI seed loads a draft) without clobbering
    // the cursor while the user types: only reset when the incoming value really
    // differs from what the editor currently holds.
    useEffect(() => {
        if (!editor) return;
        const current = getMarkdown(editor);
        if (value !== current) {
            // #320 — this passed `false`, which in the OLD tiptap signature meant
            // "don't emit an update". The 2nd argument is now an options object, so
            // `false` was type-invalid and the flag silently stopped applying:
            // syncing the prop back into the editor re-fired onUpdate. `emitUpdate`
            // restores the intent — a programmatic sync is not a user edit.
            editor.commands.setContent(value || '', { emitUpdate: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, editor]);

    if (!editor) return null;

    const pickImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !onImageUpload) return;
        if (!file.type.startsWith('image/')) { setImageError('Endast bildfiler stöds.'); return; }
        setUploadingImage(true);
        setImageError(null);
        try {
            const url = await onImageUpload(file);
            editor.chain().focus().setImage({ src: url }).run();
        } catch (err: any) {
            setImageError(err?.message || 'Bilduppladdningen misslyckades.');
        } finally {
            setUploadingImage(false);
        }
    };

    const linkSelection = () => {
        const prev = editor.getAttributes('link').href as string | undefined;
        const url = window.prompt('Länkadress (URL)', prev || 'https://');
        if (url === null) return;
        if (url.trim() === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
    };

    return (
        <div className="border border-gray-300 rounded-xl focus-within:ring-2 focus-within:ring-[#1A4D4F] focus-within:border-transparent transition-shadow bg-white">
            <div className="relative flex items-center gap-0.5 px-2 py-1.5 border-b border-gray-100 bg-gray-50/70 flex-wrap rounded-t-xl">
                <ToolbarButton title="Fet" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="w-4 h-4" /></ToolbarButton>
                <ToolbarButton title="Kursiv" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="w-4 h-4" /></ToolbarButton>
                <span className="w-px h-5 bg-gray-200 mx-1" />
                <ToolbarButton title="Rubrik" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="w-4 h-4" /></ToolbarButton>
                <ToolbarButton title="Underrubrik" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="w-4 h-4" /></ToolbarButton>
                <span className="w-px h-5 bg-gray-200 mx-1" />
                <ToolbarButton title="Punktlista" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="w-4 h-4" /></ToolbarButton>
                <ToolbarButton title="Numrerad lista" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="w-4 h-4" /></ToolbarButton>
                <ToolbarButton title="Länk" active={editor.isActive('link')} onClick={linkSelection}><LinkIcon className="w-4 h-4" /></ToolbarButton>
                {onImageUpload && (
                    <ToolbarButton title="Infoga bild" disabled={uploadingImage} onClick={() => fileInputRef.current?.click()}>
                        {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                    </ToolbarButton>
                )}
                <ToolbarButton title="Ångra" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}><Undo2 className="w-4 h-4" /></ToolbarButton>
                <ToolbarButton title="Gör om" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}><Redo2 className="w-4 h-4" /></ToolbarButton>
                <span className="flex-1" />
                {toolbarExtra}
            </div>
            {/* Selection bubble menu — quick formatting + a selection-scoped Link action. */}
            <BubbleMenu
                editor={editor}
                className="flex items-center gap-0.5 rounded-lg border border-gray-200 bg-white shadow-lg p-1"
            >
                <ToolbarButton title="Fet" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="w-4 h-4" /></ToolbarButton>
                <ToolbarButton title="Kursiv" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="w-4 h-4" /></ToolbarButton>
                <ToolbarButton title="Länk" active={editor.isActive('link')} onClick={linkSelection}><LinkIcon className="w-4 h-4" /></ToolbarButton>
                {selectionAssistRun && (
                    <>
                        <span className="w-px h-5 bg-gray-200 mx-0.5" />
                        <LinkAssistPopover
                            align="left"
                            subjectText={() => {
                                const { from, to } = editor.state.selection;
                                return editor.state.doc.textBetween(from, to, '\n');
                            }}
                            run={(action, instruction) => {
                                const { from, to } = editor.state.selection;
                                selRangeRef.current = { from, to };
                                const text = editor.state.doc.textBetween(from, to, '\n');
                                return selectionAssistRun(action, instruction, text);
                            }}
                            onApply={(revised) => {
                                // Replace ONLY the originally-selected range, not the whole doc.
                                const range = selRangeRef.current;
                                if (range) {
                                    editor.chain().focus().insertContentAt(range, revised).run();
                                } else {
                                    editor.chain().focus().insertContent(revised).run();
                                }
                                selRangeRef.current = null;
                            }}
                        />
                    </>
                )}
            </BubbleMenu>

            {/* Empty-line floating menu — insert block elements. */}
            <FloatingMenu
                editor={editor}
                className="flex items-center gap-0.5 rounded-lg border border-gray-200 bg-white shadow-lg p-1"
            >
                <ToolbarButton title="Rubrik" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="w-4 h-4" /></ToolbarButton>
                <ToolbarButton title="Underrubrik" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="w-4 h-4" /></ToolbarButton>
                <ToolbarButton title="Punktlista" onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="w-4 h-4" /></ToolbarButton>
                {onImageUpload && (
                    <ToolbarButton title="Infoga bild" disabled={uploadingImage} onClick={() => fileInputRef.current?.click()}><ImagePlus className="w-4 h-4" /></ToolbarButton>
                )}
            </FloatingMenu>

            {/* Content clips to the rounded bottom; the toolbar above intentionally
                does NOT clip so the "Fråga Link" popover can overflow the editor. */}
            <div className="rounded-b-xl overflow-hidden">
                <EditorContent editor={editor} />
                {imageError && <p className="px-3 pb-2 text-xs text-red-600">{imageError}</p>}
            </div>
            {onImageUpload && (
                <input ref={fileInputRef} type="file" accept="image/*" onChange={pickImage} className="hidden" />
            )}
        </div>
    );
};

export default MarkdownEditor;
